<template>
  <div class="bita-form-page">
    <!-- Hero -->
    <div class="form-hero">
      <p class="form-crumb">
        <span class="form-crumb__link" @click="goBack">Bitácoras</span>
        <v-icon small class="form-crumb__sep">chevron_right</v-icon>
        {{ form.id ? 'Editar' : 'Nueva' }}
      </p>
      <h1 class="form-title">
        {{ form.id ? 'Editar Plantilla' : 'Nueva Plantilla' }}
      </h1>
      <p class="form-subtitle">
        Define los datos y el flujo de pasos. Así verá el transportista la
        bitácora en la app, paso a paso.
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="form-loading">
      <v-progress-circular
        indeterminate
        color="#254F87"
        size="36"
      ></v-progress-circular>
      <span>Cargando plantilla...</span>
    </div>

    <template v-else>
      <!-- Datos generales -->
      <div class="form-card">
        <h3 class="card-title">Datos de la plantilla</h3>
        <div class="form-grid">
          <div class="form-field form-field--wide">
            <label class="form-label">Nombre <span class="req">*</span></label>
            <v-text-field
              v-model="form.name"
              solo
              flat
              hide-details
              class="f-input"
              placeholder="Ej. Cargas refrigeradas"
            ></v-text-field>
          </div>
          <div class="form-field">
            <label class="form-label">Tipo</label>
            <v-select
              v-model="form.assignmentType"
              :items="typeOptions"
              solo
              flat
              hide-details
              class="f-input"
            ></v-select>
          </div>
        </div>

        <div class="toggles-row">
          <label class="toggle">
            <v-switch
              v-model="form.isDefault"
              hide-details
              color="#e5252a"
              class="ma-0 pa-0"
            ></v-switch>
            <span>Por defecto para este tipo</span>
          </label>
          <label class="toggle">
            <v-switch
              v-model="form.enabled"
              hide-details
              color="#1f9d63"
              class="ma-0 pa-0"
            ></v-switch>
            <span>Activa</span>
          </label>
        </div>
        <p v-if="form.isDefault" class="default-hint">
          <v-icon x-small>info</v-icon>
          Marcar como default desmarcará la plantilla default anterior de este
          tipo.
        </p>
      </div>

      <!-- Pasos -->
      <div class="form-card">
        <div class="steps-head">
          <div>
            <h3 class="card-title">Flujo de pasos</h3>
            <p class="steps-sub">
              Cada paso puede exigir foto, comentario, firma o nombre de quien
              recibe. Un paso puede mover el estado del envío.
            </p>
          </div>
          <span class="steps-count-badge"
            >{{ steps.length }} paso{{ steps.length === 1 ? '' : 's' }}</span
          >
        </div>

        <!-- Arranque rápido (sin pasos aún) -->
        <div v-if="!steps.length" class="quick-start">
          <button type="button" class="qs-card" @click="addStep">
            <span class="qs-card__icon-wrap">
              <v-icon class="qs-card__icon">edit_note</v-icon>
            </span>
            <span class="qs-card__title">Empezar desde cero</span>
            <span class="qs-card__hint">Agrega tus pasos uno a uno</span>
          </button>
          <button
            type="button"
            class="qs-card qs-card--accent"
            @click="loadCargasBase"
          >
            <span class="qs-card__icon-wrap qs-card__icon-wrap--accent">
              <v-icon class="qs-card__icon">auto_awesome</v-icon>
            </span>
            <span class="qs-card__title">Usar plantilla “Cargas”</span>
            <span class="qs-card__hint">18 pasos listos para ajustar</span>
          </button>
        </div>

        <!-- Timeline de pasos -->
        <div v-else class="timeline">
          <div v-for="(step, i) in steps" :key="i" class="tl-step">
            <div class="tl-rail">
              <span
                class="tl-dot"
                :class="{ 'tl-dot--moves': step.movesStatus }"
                >{{ i + 1 }}</span
              >
              <span v-if="i < steps.length - 1" class="tl-line"></span>
            </div>
            <div class="tl-card">
              <div class="tl-card__top">
                <v-text-field
                  v-model="step.title"
                  solo
                  flat
                  hide-details
                  class="f-input tl-title"
                  placeholder="Título del paso"
                ></v-text-field>
                <div class="tl-actions">
                  <v-btn
                    icon
                    small
                    :disabled="i === 0"
                    title="Subir"
                    @click="moveStep(i, -1)"
                  >
                    <v-icon small>keyboard_arrow_up</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="i === steps.length - 1"
                    title="Bajar"
                    @click="moveStep(i, 1)"
                  >
                    <v-icon small>keyboard_arrow_down</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    class="tl-remove"
                    title="Quitar paso"
                    @click="removeStep(i)"
                  >
                    <v-icon small>close</v-icon>
                  </v-btn>
                </div>
              </div>
              <div class="tl-card__reqs">
                <button
                  type="button"
                  class="req-pill"
                  :class="{ 'req-pill--on': step.requiresPhoto }"
                  @click="step.requiresPhoto = !step.requiresPhoto"
                >
                  <v-icon x-small>photo_camera</v-icon> Foto
                </button>
                <button
                  type="button"
                  class="req-pill"
                  :class="{ 'req-pill--on': step.requiresComment }"
                  @click="step.requiresComment = !step.requiresComment"
                >
                  <v-icon x-small>chat_bubble_outline</v-icon> Comentario
                </button>
                <button
                  type="button"
                  class="req-pill"
                  :class="{ 'req-pill--on': step.requiresSignature }"
                  @click="step.requiresSignature = !step.requiresSignature"
                >
                  <v-icon x-small>gesture</v-icon> Firma
                </button>
                <button
                  type="button"
                  class="req-pill"
                  :class="{ 'req-pill--on': step.requiresReceiverName }"
                  @click="
                    step.requiresReceiverName = !step.requiresReceiverName
                  "
                >
                  <v-icon x-small>person</v-icon> Recibe
                </button>
                <div class="moves-group">
                  <v-icon x-small class="moves-icon">flag</v-icon>
                  <select
                    v-model="step.movesStatus"
                    class="moves-select"
                    :class="{ 'moves-select--on': step.movesStatus }"
                  >
                    <option :value="null">No cambia estado</option>
                    <option
                      v-for="opt in movesOptions"
                      :key="opt.value"
                      :value="opt.value"
                      >→ {{ opt.text }}</option
                    >
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div class="tl-add">
            <v-btn small class="btn-add-step" @click="addStep">
              <v-icon small left>add</v-icon>Agregar paso
            </v-btn>
            <v-btn
              v-if="!form.id"
              small
              flat
              class="btn-cargas-inline"
              @click="loadCargasBase"
            >
              <v-icon small left>auto_awesome</v-icon>Reemplazar con “Cargas”
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="form-footer">
        <v-btn class="btn-cancel" flat @click="goBack">
          <v-icon left small>close</v-icon>Cancelar
        </v-btn>
        <v-btn class="btn-save" :loading="saving" @click="save">
          <v-icon left small>save</v-icon>Guardar Plantilla
        </v-btn>
      </div>
    </template>

    <v-snackbar
      v-model="snack.show"
      :timeout="3500"
      :color="snack.color"
      right
      top
    >
      {{ snack.message }}
      <v-btn flat dark @click="snack.show = false">Cerrar</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import Api from '@/api/index'

function blankStep() {
  return {
    title: '',
    requiresPhoto: false,
    requiresComment: false,
    requiresSignature: false,
    requiresReceiverName: false,
    movesStatus: null,
  }
}

// Estados a los que un paso puede mover el envío (lista única).
// NOTA: confirmar los valores (snake_case) con el backend.
const MOVES_OPTIONS = [
  { text: 'Asignado', value: 'asignado' },
  { text: 'En tránsito', value: 'en_transito' },
  { text: 'Llegada', value: 'llegada' },
  { text: 'Evidencia', value: 'evidencia' },
  { text: 'Firma', value: 'firma' },
  { text: 'Entregado', value: 'entregado' },
]

// Plantilla de referencia "Cargas" (18 pasos) para arranque rápido
const CARGAS_STEPS = [
  { title: 'Camión en espera de asignación de rampa' },
  { title: 'Rampa asignada al camión', photo: true },
  { title: 'Equipo posicionado en rampa de carga', photo: true, comment: true },
  { title: 'Verificación de temperatura del equipo', photo: true },
  { title: 'En espera de recepción de carga' },
  { title: 'Recepción de carga', photo: true, comment: true },
  { title: 'Verificación de temperatura del equipo', photo: true },
  { title: 'Aseguramiento de carga', photo: true, comment: true },
  { title: 'Carga de pallets al camión', photo: true, comment: true },
  { title: 'En espera de documentación' },
  { title: 'Documentación recibida' },
  { title: 'Salida hacia aduanas', moves: 'en_transito' },
  { title: 'Ingreso a aduanas para aforo' },
  { title: 'Salida de aduanas con destino a la bodega del cliente' },
  { title: 'Arribo a bodega del cliente', moves: 'llegada' },
  { title: 'Inicio de descarga', photo: true, comment: true },
  {
    title: 'Finalización de descarga',
    photo: true,
    comment: true,
    signature: true,
    receiver: true,
  },
  { title: 'Servicio finalizado', moves: 'entregado' },
]

export default {
  name: 'BitacoraTemplateForm',
  data() {
    return {
      loading: false,
      saving: false,
      typeOptions: [
        { text: 'Transportista', value: 'transportista' },
        { text: 'Mensajero', value: 'mensajero' },
        { text: 'Ambos / Sin tipo', value: null },
      ],
      form: {
        id: null,
        name: '',
        assignmentType: 'transportista',
        isDefault: false,
        enabled: true,
      },
      steps: [],
      snack: { show: false, color: 'info', message: '' },
    }
  },
  computed: {
    movesOptions() {
      return MOVES_OPTIONS
    },
  },
  created() {
    const id = this.$route.params.id
    if (id != null) {
      this.loadTemplate(id)
    } else {
      this.steps = []
    }
  },
  methods: {
    notify(message, color = 'info') {
      this.snack = { show: true, color, message }
    },
    isHttpError(res) {
      return !!(res && typeof res.status === 'number' && res.status >= 400)
    },
    goBack() {
      this.$router.push('/admin/bitacoras')
    },
    async loadTemplate(id) {
      this.loading = true
      try {
        const res = await Api.BitacoraTemplate.getById(id)
        const tpl = res && res.data ? res.data : res
        if (!tpl || tpl.id == null) {
          this.notify('No se pudo cargar la plantilla', 'error')
          this.goBack()
          return
        }
        this.form = {
          id: tpl.id,
          name: tpl.name || '',
          assignmentType:
            tpl.assignmentType != null ? tpl.assignmentType : null,
          isDefault: !!tpl.isDefault,
          enabled: tpl.enabled !== false,
        }
        this.steps = (tpl.steps || [])
          .slice()
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .map(s => ({
            title: s.title || '',
            requiresPhoto: !!s.requiresPhoto,
            requiresComment: !!s.requiresComment,
            requiresSignature: !!s.requiresSignature,
            requiresReceiverName: !!s.requiresReceiverName,
            movesStatus: s.movesStatus || null,
          }))
      } catch (e) {
        this.notify('Error al cargar la plantilla', 'error')
        this.goBack()
      } finally {
        this.loading = false
      }
    },
    addStep() {
      this.steps.push(blankStep())
    },
    loadCargasBase() {
      this.steps = CARGAS_STEPS.map(s => ({
        title: s.title,
        requiresPhoto: !!s.photo,
        requiresComment: !!s.comment,
        requiresSignature: !!s.signature,
        requiresReceiverName: !!s.receiver,
        movesStatus: s.moves || null,
      }))
      if (!this.form.name) this.form.name = 'Bitácora para entrega de carga'
    },
    removeStep(index) {
      this.steps.splice(index, 1)
    },
    moveStep(index, dir) {
      const target = index + dir
      if (target < 0 || target >= this.steps.length) return
      const [moved] = this.steps.splice(index, 1)
      this.steps.splice(target, 0, moved)
    },
    buildPayload() {
      return {
        name: (this.form.name || '').trim(),
        assignment_type: this.form.assignmentType || null,
        is_default: !!this.form.isDefault,
        enabled: !!this.form.enabled,
        steps: this.steps.map((s, i) => ({
          position: i + 1,
          title: (s.title || '').trim(),
          requires_photo: !!s.requiresPhoto,
          requires_comment: !!s.requiresComment,
          requires_signature: !!s.requiresSignature,
          requires_receiver_name: !!s.requiresReceiverName,
          moves_status: s.movesStatus || null,
        })),
      }
    },
    validate(payload) {
      if (!payload.name) {
        this.notify('El nombre es obligatorio.', 'error')
        return false
      }
      if (!payload.steps.length) {
        this.notify('Agrega al menos un paso.', 'error')
        return false
      }
      if (payload.steps.some(s => !s.title)) {
        this.notify('Cada paso requiere un título.', 'error')
        return false
      }
      return true
    },
    async save() {
      const payload = this.buildPayload()
      if (!this.validate(payload)) return
      this.saving = true
      try {
        const res = this.form.id
          ? await Api.BitacoraTemplate.update(this.form.id, payload)
          : await Api.BitacoraTemplate.create(payload)
        if (this.isHttpError(res)) throw new Error('http')
        this.notify('Plantilla guardada correctamente', 'success')
        setTimeout(() => this.goBack(), 600)
      } catch (e) {
        this.notify('No se pudo guardar la plantilla', 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.bita-form-page {
  padding: 24px 28px 40px;
  background: #f8fafc;
  min-height: 100vh;
  color: #172133;
}

/* Hero */
.form-hero {
  margin-bottom: 20px;
}
.form-crumb {
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 0 0 6px;
  font-size: 13px;
  color: #6b7280;
}
.form-crumb__link {
  cursor: pointer;
  color: #4b5d80;
  font-weight: 600;
}
.form-crumb__link:hover {
  text-decoration: underline;
}
.form-crumb__sep {
  color: #b4bdc9 !important;
  font-size: 16px !important;
}
.form-title {
  margin: 0 0 4px;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.15;
}
.form-subtitle {
  margin: 0;
  font-size: 13.5px;
  color: #64748b;
  max-width: 760px;
}

.form-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 60px 20px;
  color: #64748b;
  font-size: 14px;
}

/* Cards */
.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 22px 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.card-title {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 700;
  color: #172133;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.form-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 14px;
}
.form-field {
  display: flex;
  flex-direction: column;
}
.form-label {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 6px;
}
.req {
  color: #e5252a;
}
.f-input {
  border: 1px solid #d1d5db;
  border-radius: 10px;
}
.f-input.v-text-field,
.f-input.v-select {
  padding: 0 12px;
}

.toggles-row {
  display: flex;
  gap: 28px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
}
.default-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 12px 0 0;
  font-size: 12px;
  color: #92711b;
  background: #fef9e7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 8px 10px;
}
.default-hint .v-icon {
  color: #d9a40a !important;
}

/* Steps head */
.steps-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.steps-sub {
  margin: 4px 0 0;
  font-size: 12px;
  color: #94a3b8;
  max-width: 620px;
}
.steps-count-badge {
  flex-shrink: 0;
  background: #eef4ff;
  color: #254f87;
  border: 1px solid #d6e3fb;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
}
.btn-add-step {
  border: 1px solid rgba(37, 79, 135, 0.3) !important;
  color: #254f87 !important;
  background: rgba(37, 79, 135, 0.08) !important;
  box-shadow: none !important;
  text-transform: none !important;
  font-weight: 600 !important;
}
.btn-cargas-inline {
  color: #94a3b8 !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

/* Arranque rápido */
.quick-start {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.qs-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 26px 16px;
  border: 1.5px dashed #d6deea;
  border-radius: 14px;
  background: #fcfdff;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
}
.qs-card:hover {
  border-color: #254f87;
  background: #f5f9ff;
  transform: translateY(-1px);
}
.qs-card--accent:hover {
  border-color: #e5252a;
  background: #fff6f6;
}
.qs-card__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #eef4ff;
  margin-bottom: 2px;
}
.qs-card__icon-wrap--accent {
  background: #fdecec;
}
.qs-card__icon {
  color: #254f87 !important;
  font-size: 24px !important;
}
.qs-card--accent .qs-card__icon {
  color: #e5252a !important;
}
.qs-card__title {
  font-size: 14px;
  font-weight: 700;
  color: #172133;
}
.qs-card__hint {
  font-size: 12px;
  color: #94a3b8;
}

/* Timeline */
.tl-step {
  display: flex;
  gap: 12px;
}
.tl-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 28px;
}
.tl-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #254f87;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 0 0 4px rgba(37, 79, 135, 0.1);
  margin-top: 4px;
}
.tl-dot--moves {
  background: #c79a2b;
  box-shadow: 0 0 0 4px rgba(199, 154, 43, 0.15);
}
.tl-line {
  width: 2px;
  flex: 1;
  background: #e2e8f0;
  margin: 4px 0;
  min-height: 12px;
}
.tl-card {
  flex: 1;
  min-width: 0;
  border: 1px solid #e8eaef;
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}
.tl-card__top {
  display: flex;
  align-items: center;
  gap: 6px;
}
.tl-title {
  flex: 1;
}
.tl-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.tl-remove {
  color: #e5252a !important;
}
.tl-card__reqs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.req-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.12s ease;
}
.req-pill .v-icon {
  color: inherit !important;
}
.req-pill:hover {
  border-color: #cbd5e1;
}
.req-pill--on {
  background: #eef4ff;
  border-color: #254f87;
  color: #254f87;
}
.req-pill--on .v-icon {
  color: #254f87 !important;
}
.moves-group {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 2px 6px 2px 10px;
}
.moves-icon {
  color: #94a3b8 !important;
}
.moves-select {
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  outline: none;
  cursor: pointer;
  padding: 3px 2px;
}
.moves-select--on {
  color: #c79a2b;
}
.tl-add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 2px;
  padding-left: 40px;
}

/* Footer */
.form-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 4px;
}
.btn-cancel {
  border: 1px solid #e2e8f0 !important;
  background: #fff !important;
  color: #475569 !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  height: 42px !important;
  box-shadow: none !important;
}
.btn-save {
  background: #e5252a !important;
  color: #fff !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  height: 42px !important;
  padding: 0 22px !important;
  box-shadow: 0 4px 12px rgba(229, 37, 42, 0.2) !important;
}

@media (max-width: 720px) {
  .bita-form-page {
    padding: 16px 14px 32px;
  }
  .form-grid {
    grid-template-columns: 1fr;
  }
  .quick-start {
    grid-template-columns: 1fr;
  }
  .moves-group {
    margin-left: 0;
  }
  .tl-add {
    padding-left: 0;
    flex-direction: column;
  }
  .form-footer {
    flex-direction: column-reverse;
  }
  .btn-cancel,
  .btn-save {
    width: 100%;
    justify-content: center;
  }
}
</style>
